import { ConvertExperimentEnvironment, Environment } from './types.d';

export enum Experiment {
  BiggerNextRequestButton = 'BIGGER_NEXT_REQUEST_BUTTON',
  BlogValueProposition = 'BLOG_VALUE_PROPOSITION',
  ChatFlow = 'CHAT_FLOW',
  DisplayTiming = 'DISPLAY_TIMING',
  MikittyVertical = 'MIKITTY_VERTICAL',
  PhoneLogin = 'PHONE_LOGIN',
  PhoneRequired = 'PHONE_REQUIRED',
  PricingValueProposition = 'PRICING_VALUE_PROPOSITION',
  SecureDataUsageValueProposition = 'SECURE_DATA_USAGE_VALUE_PROPOSITION',
  ShowRequestSummary = 'SHOW_REQUEST_SUMMARY',
}

export enum PhoneLoginVariants {
  original = 'original',
  phoneLogin = 'phoneLogin',
}

export enum PhoneRequiredVariants {
  original = 'original',
  phoneRequired = 'phoneRequired',
}

export enum ChatFlowVariants {
  chat = 'chat',
  hybridChat = 'hybridChat',
  original = 'original',
}

export enum PricingValuePropositionVariants {
  interstitialStepPricing = 'interstitialStepPricing',
  landingPagePricing = 'landingPagePricing',
  lpAndInterstitialStepValuePricing = 'lpAndInterstitialStepValuePricing',
  original = 'original',
}

export enum BlogCTAValuePropositionVariants {
  prod4477Original = 'prod4477Original',
  prod4477ValueProposition = 'prod4477ValueProposition',
  prod4477ValuePropositionAddressInput = 'prod4477ValuePropositionAddressInput',
}

export enum SecureDataUsageValuePropositionVariants {
  secureDataValuePropOriginal = 'secureDataValuePropOriginal',
  secureDataValuePropVariant = 'secureDataValuePropVariant',
}

export enum DisplayTimingVariants {
  displayTimingOriginal = 'displayTimingOriginal',
  displayTimingVariant = 'displayTimingVariant',
}

export enum MikittyVerticalVariants {
  mikitty = 'mikitty',
  original = 'original',
}

export enum ShowRequestSummaryVariants {
  original = 'original',
  withSummaryPage = 'withSummaryPage',
}

export enum BiggerNextRequestButtonVariants {
  BiggerNextRequestButtonOriginal = 'BiggerNextRequestButtonOriginal',
  BiggerNextRequestButtonVariant = 'BiggerNextRequestButtonVariant',
}

export type VariantsMap = {
  [Experiment.PhoneLogin]: typeof PhoneLoginVariants;
  [Experiment.PricingValueProposition]: typeof PricingValuePropositionVariants;
  [Experiment.PhoneRequired]: typeof PhoneRequiredVariants;
  [Experiment.BlogValueProposition]: typeof BlogCTAValuePropositionVariants;
  [Experiment.SecureDataUsageValueProposition]: typeof SecureDataUsageValuePropositionVariants;
  [Experiment.ChatFlow]: typeof ChatFlowVariants;
  [Experiment.DisplayTiming]: typeof DisplayTimingVariants;
  [Experiment.MikittyVertical]: typeof MikittyVerticalVariants;
  [Experiment.ShowRequestSummary]: typeof ShowRequestSummaryVariants;
  [Experiment.BiggerNextRequestButton]: typeof BiggerNextRequestButtonVariants;
};

export const CONFIGS: {
  [E in Experiment]: {
    [env in Environment]: ConvertExperimentEnvironment;
  };
} = {
  [Experiment.PhoneLogin]: {
    [Environment.PreProd]: {
      experimentId: '100414264',
      variants: {
        original: '100440353',
        phoneLogin: '100440354',
      },
    },
    [Environment.Prod]: {
      experimentId: '100414265',
      variants: {
        original: '100440355',
        phoneLogin: '100440356',
      },
    },
  },
  [Experiment.PricingValueProposition]: {
    [Environment.PreProd]: {
      experimentId: '100410330',
      variants: {
        original: '100431069',
        landingPagePricing: '100431070',
        interstitialStepPricing: '100431071',
        lpAndInterstitialStepValuePricing: '100431075',
      },
    },
    [Environment.Prod]: {
      experimentId: '100410612',
      variants: {
        original: '100431705',
        landingPagePricing: '100431706',
        interstitialStepPricing: '100431709',
        lpAndInterstitialStepValuePricing: '100431710',
      },
    },
  },
  [Experiment.PhoneRequired]: {
    [Environment.PreProd]: {
      experimentId: '100410933',
      variants: {
        original: '100432464',
        phoneRequired: '100432465',
      },
    },
    [Environment.Prod]: {
      experimentId: '100410934',
      variants: {
        original: '100432466',
        phoneRequired: '100432467',
      },
    },
  },
  [Experiment.BlogValueProposition]: {
    [Environment.PreProd]: {
      experimentId: '100412017',
      variants: {
        prod4477Original: '100434851',
        prod4477ValuePropositionAddressInput: '100434852',
        prod4477ValueProposition: '100434854',
      },
    },
    [Environment.Prod]: {
      experimentId: '100412666',
      variants: {
        prod4477Original: '100436505',
        prod4477ValuePropositionAddressInput: '100436506',
        prod4477ValueProposition: '100436507',
      },
    },
  },
  [Experiment.SecureDataUsageValueProposition]: {
    [Environment.PreProd]: {
      experimentId: '100414279',
      variants: {
        secureDataValuePropOriginal: '100440392',
        secureDataValuePropVariant: '100440393',
      },
    },
    [Environment.Prod]: {
      experimentId: '100414281',
      variants: {
        secureDataValuePropOriginal: '100440396',
        secureDataValuePropVariant: '100440397',
      },
    },
  },
  [Experiment.ChatFlow]: {
    [Environment.PreProd]: {
      experimentId: '100414505',
      variants: {
        original: '100440873',
        chat: '100440874',
        hybridChat: '100440876',
      },
    },
    [Environment.Prod]: {
      experimentId: '100414506',
      variants: {
        original: '100440878',
        chat: '100440879',
        hybridChat: '100440880',
      },
    },
  },
  [Experiment.MikittyVertical]: {
    [Environment.PreProd]: {
      experimentId: '100414287',
      variants: {
        mikitty: '100440410',
        original: '100440409',
      },
    },
    [Environment.Prod]: {
      experimentId: '100414518',
      variants: {
        mikitty: '100440915',
        original: '100440914',
      },
    },
  },
  [Experiment.DisplayTiming]: {
    [Environment.PreProd]: {
      experimentId: '100413619',
      variants: {
        displayTimingOriginal: '100438629',
        displayTimingVariant: '100438630',
      },
    },
    [Environment.Prod]: {
      experimentId: '100414698',
      variants: {
        displayTimingOriginal: '100441306',
        displayTimingVariant: '100441307',
      },
    },
  },
  [Experiment.ShowRequestSummary]: {
    [Environment.PreProd]: {
      experimentId: '100416695',
      variants: {
        original: '100445959',
        withSummaryPage: '100445960',
      },
    },
    [Environment.Prod]: {
      experimentId: '100417168',
      variants: {
        original: '100447048',
        withSummaryPage: '100447049',
      },
    },
  },
  [Experiment.BiggerNextRequestButton]: {
    [Environment.PreProd]: {
      experimentId: '100419794',
      variants: {
        BiggerNextRequestButtonOriginal: '100453026',
        BiggerNextRequestButtonVariant: '100453027',
      },
    },
    [Environment.Prod]: {
      experimentId: '100419328',
      variants: {
        BiggerNextRequestButtonOriginal: '100452019',
        BiggerNextRequestButtonVariant: '100452020',
      },
    },
  },
};

export enum Goal {
  loginStep1Passed = 'loginStep1Passed',
  matchingAnimationViewed = 'matchingAnimationViewed',
  nameClicked = 'nameClicked',
  newRequestCreated = 'newRequestCreated',
  newRequestSubmitted = 'newRequestSubmitted',
  phoneNumberClicked = 'phoneNumberClicked',
  startNewRequestClicked = 'startNewRequestClicked',
}

export const GOAL_CONFIGS: { [env in Environment]: { [goal in Goal]: string } } = {
  [Environment.PreProd]: {
    [Goal.loginStep1Passed]: '10049114',
    [Goal.matchingAnimationViewed]: '100412809',
    [Goal.nameClicked]: '100412415',
    [Goal.newRequestCreated]: '10049115',
    [Goal.newRequestSubmitted]: '100412414',
    [Goal.phoneNumberClicked]: '100412419',
    [Goal.startNewRequestClicked]: '10049465',
  },
  [Environment.Prod]: {
    [Goal.loginStep1Passed]: '10049116',
    [Goal.matchingAnimationViewed]: '100412810',
    [Goal.nameClicked]: '100412416',
    [Goal.newRequestCreated]: '10049117',
    [Goal.newRequestSubmitted]: '100412417',
    [Goal.phoneNumberClicked]: '100412418',
    [Goal.startNewRequestClicked]: '10049470',
  },
};
